import { NEW_CARS_SUPPLIER } from "@/app/listing/utils/eu-markets";

/**
 * Represents the active filters selected by the user
 */
export class ActiveFiltersView {
	private _makes: string[] = [];
	private _models: string[] = [];
	private _trims: string[] = [];
	private _suppliers: string[] = [];
	private _fuelTypes: string[] = [];
	private _transmissions: string[] = [];
	private _saleTypes: string[] = [];
	private _colors: string[] = [];
	private _doors: string[] = [];
	private _seats: string[] = [];
	private _minYear?: number;
	private _maxYear?: number;
	private _minPrice?: number;
	private _maxPrice?: number;
	private _minSellPrice?: number;
	private _maxSellPrice?: number;
	private _minMileage?: number;
	private _maxMileage?: number;
	private _minEnginePower?: number;
	private _maxEnginePower?: number;
	private _minCo2Emission?: number;
	private _maxCo2Emission?: number;
	private _onlyFavorites: boolean = false;
	private _markets: string[] = [];
	private _hasInspectionReport?: boolean;
	private _hideDamaged?: boolean;
	private _hideNonOperable?: boolean;
	private _onlyDamaged?: boolean;
	private _onlyNonOperable?: boolean;
	private _appraisalStatus?: string;
	private _isBrandNew: boolean = false;
	private _searchText?: string;

	// Vehicle grade (NAMA 1–5, 1 = best). Strict by default: when grades are
	// selected, ungraded listings are excluded unless _includeUngraded is set.
	private _conditionGrades: number[] = [];
	private _includeUngraded: boolean = false;

	// V2 location filter (plan §2.4). Mode A uses regionCodes/provinceCodes;
	// Mode B uses centerLat + centerLng + radiusKm. Modes are mutually
	// exclusive — frontend never sends both (the controller 400s if it did).
	private _regionCodes: string[] = [];
	private _provinceCodes: string[] = [];
	private _centerLat?: number;
	private _centerLng?: number;
	private _centerLabel?: string;
	private _radiusKm?: number;

	constructor() {
		// Initialize with empty filters
	}

	/**
	 * Create an ActiveFiltersView instance from URL search params
	 */
	static fromQueryParams(params: URLSearchParams): ActiveFiltersView {
		const filters = new ActiveFiltersView();

		// Parse array filters
		const makes = params.get("makes");
		if (makes) filters.setMakes(makes.split(","));

		const models = params.get("models");
		if (models) filters.setModels(models.split(","));

		const trims = params.get("trims");
		if (trims) filters.setTrims(trims.split(","));

		const suppliers = params.get("suppliers");
		if (suppliers) filters.setSuppliers(suppliers.split(","));

		const fuelTypes = params.get("fuelTypes");
		if (fuelTypes) filters.setFuelTypes(fuelTypes.split(","));

		const transmissions = params.get("transmissions");
		if (transmissions) filters.setTransmissions(transmissions.split(","));

		const saleTypes = params.get("saleTypes");
		if (saleTypes) filters.setSaleTypes(saleTypes.split(","));

		const colors = params.get("colors");
		if (colors) filters.setColors(colors.split(","));

		const doors = params.get("doors");
		if (doors) filters.setDoors(doors.split(","));

		const seats = params.get("seats");
		if (seats) filters.setSeats(seats.split(","));

		const markets = params.get("markets");
		if (markets) filters.setMarkets(markets.split(","));

		// Parse range filters
		const minYear = params.get("minYear");
		const maxYear = params.get("maxYear");
		if (minYear || maxYear) {
			filters.setYearRange(
				minYear ? Number.parseInt(minYear, 10) : undefined,
				maxYear ? Number.parseInt(maxYear, 10) : undefined
			);
		}

		const minPrice = params.get("minPrice");
		const maxPrice = params.get("maxPrice");
		if (minPrice || maxPrice) {
			filters.setPriceRange(
				minPrice ? Number.parseInt(minPrice, 10) : undefined,
				maxPrice ? Number.parseInt(maxPrice, 10) : undefined
			);
		}

		const minSellPrice = params.get("minSellPrice");
		const maxSellPrice = params.get("maxSellPrice");
		if (minSellPrice || maxSellPrice) {
			filters.setSellPriceRange(
				minSellPrice ? Number.parseInt(minSellPrice, 10) : undefined,
				maxSellPrice ? Number.parseInt(maxSellPrice, 10) : undefined
			);
		}

		const minMileage = params.get("minMileage");
		const maxMileage = params.get("maxMileage");
		if (minMileage || maxMileage) {
			filters.setMileageRange(
				minMileage ? Number.parseInt(minMileage, 10) : undefined,
				maxMileage ? Number.parseInt(maxMileage, 10) : undefined
			);
		}

		const minEnginePower = params.get("minEnginePower");
		const maxEnginePower = params.get("maxEnginePower");
		if (minEnginePower || maxEnginePower) {
			filters.setEnginePowerRange(
				minEnginePower ? Number.parseInt(minEnginePower, 10) : undefined,
				maxEnginePower ? Number.parseInt(maxEnginePower, 10) : undefined
			);
		}

		const minCo2Emission = params.get("minCo2Emission");
		const maxCo2Emission = params.get("maxCo2Emission");
		if (minCo2Emission || maxCo2Emission) {
			filters.setCo2EmissionRange(
				minCo2Emission ? Number.parseInt(minCo2Emission, 10) : undefined,
				maxCo2Emission ? Number.parseInt(maxCo2Emission, 10) : undefined
			);
		}

		// Parse boolean filters
		const onlyFavorites = params.get("onlyFavorites");
		if (onlyFavorites === "true") {
			filters.setOnlyFavorites(true);
		}

		const hasInspectionReport = params.get("hasInspectionReport");
		if (hasInspectionReport !== null) {
			filters.setHasInspectionReport(hasInspectionReport === "true");
		}

		const hideDamaged = params.get("hideDamaged");
		if (hideDamaged !== null) {
			filters.setHideDamaged(hideDamaged === "true");
		}

		const hideNonOperable = params.get("hideNonOperable");
		if (hideNonOperable !== null) {
			filters.setHideNonOperable(hideNonOperable === "true");
		}

		const onlyDamaged = params.get("onlyDamaged");
		if (onlyDamaged !== null) {
			filters.setOnlyDamaged(onlyDamaged === "true");
		}

		const onlyNonOperable = params.get("onlyNonOperable");
		if (onlyNonOperable !== null) {
			filters.setOnlyNonOperable(onlyNonOperable === "true");
		}

		// Parse string filters
		const appraisalStatus = params.get("appraisalStatus");
		if (appraisalStatus) {
			filters.setAppraisalStatus(appraisalStatus);
		}

		const isBrandNew = params.get("isBrandNew");
		if (isBrandNew === "true") {
			filters.setIsBrandNew(true);
			// Enforce invariant: isBrandNew overrides suppliers at API level,
			// so clear any supplier selection to keep URL and UI consistent
			filters.setSuppliers([]);
		}

		const searchText = params.get("searchText");
		if (searchText) {
			filters.setSearchText(searchText);
		}

		const conditionGrades = params.get("conditionGrades");
		// Clamp to the backend-validated NAMA range (1–5) and dedupe so a crafted
		// URL (e.g. ?conditionGrades=999) can't trigger an avoidable 400 from the
		// nest DTO's Min/Max validators.
		const parsedConditionGrades = conditionGrades
			? Array.from(
					new Set(
						conditionGrades
							.split(",")
							.map(grade => Number.parseInt(grade, 10))
							.filter(
								grade => Number.isInteger(grade) && grade >= 1 && grade <= 5
							)
					)
				)
			: [];
		if (parsedConditionGrades.length > 0) {
			filters.setConditionGrades(parsedConditionGrades);

			// includeUngraded is only meaningful alongside selected grades —
			// ignore it otherwise so a crafted URL can't leave a latent flag
			// that silently narrows results once grades are later picked.
			const includeUngraded = params.get("includeUngraded");
			if (includeUngraded !== null) {
				filters.setIncludeUngraded(includeUngraded === "true");
			}
		}

		// V2 location filter URL params
		const regionCodes = params.get("regionCodes");
		if (regionCodes) filters.setRegionCodes(regionCodes.split(","));

		const provinceCodes = params.get("provinceCodes");
		if (provinceCodes) filters.setProvinceCodes(provinceCodes.split(","));

		const centerLat = params.get("centerLat");
		const centerLng = params.get("centerLng");
		const radiusKm = params.get("radiusKm");
		if (centerLat && centerLng && radiusKm) {
			const parsedLat = Number.parseFloat(centerLat);
			const parsedLng = Number.parseFloat(centerLng);
			const parsedRadius = Number.parseFloat(radiusKm);
			if (
				Number.isFinite(parsedLat) &&
				Number.isFinite(parsedLng) &&
				Number.isFinite(parsedRadius) &&
				parsedRadius > 0
			) {
				filters.setRadius(
					parsedLat,
					parsedLng,
					parsedRadius,
					params.get("centerLabel") ?? undefined
				);
			}
		}

		return filters;
	}

	// Getters
	get makes(): string[] {
		return this._makes;
	}

	get models(): string[] {
		return this._models;
	}

	get trims(): string[] {
		return this._trims;
	}

	get suppliers(): string[] {
		return this._suppliers;
	}

	get fuelTypes(): string[] {
		return this._fuelTypes;
	}

	get transmissions(): string[] {
		return this._transmissions;
	}

	get saleTypes(): string[] {
		return this._saleTypes;
	}

	get colors(): string[] {
		return this._colors;
	}

	get doors(): string[] {
		return this._doors;
	}

	get seats(): string[] {
		return this._seats;
	}

	get markets(): string[] {
		return this._markets;
	}

	get minYear(): number | undefined {
		return this._minYear;
	}

	get maxYear(): number | undefined {
		return this._maxYear;
	}

	get minPrice(): number | undefined {
		return this._minPrice;
	}

	get maxPrice(): number | undefined {
		return this._maxPrice;
	}

	get minSellPrice(): number | undefined {
		return this._minSellPrice;
	}

	get maxSellPrice(): number | undefined {
		return this._maxSellPrice;
	}

	get minMileage(): number | undefined {
		return this._minMileage;
	}

	get maxMileage(): number | undefined {
		return this._maxMileage;
	}

	get minEnginePower(): number | undefined {
		return this._minEnginePower;
	}

	get maxEnginePower(): number | undefined {
		return this._maxEnginePower;
	}

	get minCo2Emission(): number | undefined {
		return this._minCo2Emission;
	}

	get maxCo2Emission(): number | undefined {
		return this._maxCo2Emission;
	}

	get onlyFavorites(): boolean {
		return this._onlyFavorites;
	}

	get hasInspectionReport(): boolean | undefined {
		return this._hasInspectionReport;
	}

	get hideDamaged(): boolean | undefined {
		return this._hideDamaged;
	}

	get hideNonOperable(): boolean | undefined {
		return this._hideNonOperable;
	}

	get onlyDamaged(): boolean | undefined {
		return this._onlyDamaged;
	}

	get onlyNonOperable(): boolean | undefined {
		return this._onlyNonOperable;
	}

	get appraisalStatus(): string | undefined {
		return this._appraisalStatus;
	}

	get isBrandNew(): boolean {
		return this._isBrandNew;
	}

	get searchText(): string | undefined {
		return this._searchText;
	}

	get conditionGrades(): number[] {
		return this._conditionGrades;
	}

	get includeUngraded(): boolean {
		return this._includeUngraded;
	}

	get regionCodes(): string[] {
		return this._regionCodes;
	}

	get provinceCodes(): string[] {
		return this._provinceCodes;
	}

	get centerLat(): number | undefined {
		return this._centerLat;
	}

	get centerLng(): number | undefined {
		return this._centerLng;
	}

	get centerLabel(): string | undefined {
		return this._centerLabel;
	}

	get radiusKm(): number | undefined {
		return this._radiusKm;
	}

	get hasV2LocationFilter(): boolean {
		return (
			this._regionCodes.length > 0 ||
			this._provinceCodes.length > 0 ||
			(this._centerLat !== undefined &&
				this._centerLng !== undefined &&
				this._radiusKm !== undefined)
		);
	}

	// Setters
	setMakes(makes: string[]): void {
		this._makes = makes;
	}

	setModels(models: string[]): void {
		this._models = models;
	}

	setTrims(trims: string[]): void {
		this._trims = trims;
	}

	setSuppliers(suppliers: string[]): void {
		this._suppliers = suppliers;
	}

	setFuelTypes(fuelTypes: string[]): void {
		this._fuelTypes = fuelTypes;
	}

	setTransmissions(transmissions: string[]): void {
		this._transmissions = transmissions;
	}

	setSaleTypes(saleTypes: string[]): void {
		this._saleTypes = saleTypes;
	}

	setColors(colors: string[]): void {
		this._colors = colors;
	}

	setDoors(doors: string[]): void {
		this._doors = doors;
	}

	setSeats(seats: string[]): void {
		this._seats = seats;
	}

	setMarkets(markets: string[]): void {
		this._markets = markets;
	}

	setYearRange(min?: number, max?: number): void {
		this._minYear = min;
		this._maxYear = max;
	}

	setPriceRange(min?: number, max?: number): void {
		this._minPrice = min;
		this._maxPrice = max;
	}

	setSellPriceRange(min?: number, max?: number): void {
		this._minSellPrice = min;
		this._maxSellPrice = max;
	}

	setMileageRange(min?: number, max?: number): void {
		this._minMileage = min;
		this._maxMileage = max;
	}

	setEnginePowerRange(min?: number, max?: number): void {
		this._minEnginePower = min;
		this._maxEnginePower = max;
	}

	setCo2EmissionRange(min?: number, max?: number): void {
		this._minCo2Emission = min;
		this._maxCo2Emission = max;
	}

	setOnlyFavorites(onlyFavorites: boolean): void {
		this._onlyFavorites = onlyFavorites;
	}

	setHasInspectionReport(hasInspectionReport?: boolean): void {
		this._hasInspectionReport = hasInspectionReport;
	}

	// "Hide" and "Only" are mutually exclusive per attribute: turning one on
	// clears the other so the API never receives a contradictory pair.
	setHideDamaged(hideDamaged?: boolean): void {
		this._hideDamaged = hideDamaged;
		if (hideDamaged) this._onlyDamaged = undefined;
	}

	setHideNonOperable(hideNonOperable?: boolean): void {
		this._hideNonOperable = hideNonOperable;
		if (hideNonOperable) this._onlyNonOperable = undefined;
	}

	setOnlyDamaged(onlyDamaged?: boolean): void {
		this._onlyDamaged = onlyDamaged;
		if (onlyDamaged) this._hideDamaged = undefined;
	}

	setOnlyNonOperable(onlyNonOperable?: boolean): void {
		this._onlyNonOperable = onlyNonOperable;
		if (onlyNonOperable) this._hideNonOperable = undefined;
	}

	setAppraisalStatus(appraisalStatus?: string): void {
		this._appraisalStatus = appraisalStatus;
	}

	setIsBrandNew(isBrandNew: boolean): void {
		this._isBrandNew = isBrandNew;
	}

	setSearchText(searchText?: string): void {
		this._searchText = searchText;
	}

	setConditionGrades(conditionGrades: number[]): void {
		this._conditionGrades = conditionGrades;
	}

	setIncludeUngraded(includeUngraded: boolean): void {
		this._includeUngraded = includeUngraded;
	}

	/**
	 * Mode A — apply region / province codes. Always clears Mode B so that
	 * clearing Mode A back to [] cannot leave a stale radius behind (plan §2
	 * "Mode exclusivity").
	 */
	setRegionCodes(regionCodes: string[]): void {
		this._regionCodes = regionCodes;
		this._centerLat = undefined;
		this._centerLng = undefined;
		this._centerLabel = undefined;
		this._radiusKm = undefined;
	}

	setProvinceCodes(provinceCodes: string[]): void {
		this._provinceCodes = provinceCodes;
		this._centerLat = undefined;
		this._centerLng = undefined;
		this._centerLabel = undefined;
		this._radiusKm = undefined;
	}

	/**
	 * Mode B — apply radius around a point. Clears Mode A.
	 */
	setRadius(lat: number, lng: number, radiusKm: number, label?: string): void {
		this._centerLat = lat;
		this._centerLng = lng;
		this._radiusKm = radiusKm;
		this._centerLabel = label;
		this._regionCodes = [];
		this._provinceCodes = [];
	}

	clearLocationFilter(): void {
		this._regionCodes = [];
		this._provinceCodes = [];
		this._centerLat = undefined;
		this._centerLng = undefined;
		this._centerLabel = undefined;
		this._radiusKm = undefined;
	}

	// Helper to reset all filters
	reset(): void {
		this._makes = [];
		this._models = [];
		this._trims = [];
		this._suppliers = [];
		this._fuelTypes = [];
		this._transmissions = [];
		this._saleTypes = [];
		this._colors = [];
		this._doors = [];
		this._seats = [];
		this._markets = [];
		this._minYear = undefined;
		this._maxYear = undefined;
		this._minPrice = undefined;
		this._maxPrice = undefined;
		this._minSellPrice = undefined;
		this._maxSellPrice = undefined;
		this._minMileage = undefined;
		this._maxMileage = undefined;
		this._minEnginePower = undefined;
		this._maxEnginePower = undefined;
		this._minCo2Emission = undefined;
		this._maxCo2Emission = undefined;
		this._onlyFavorites = false;
		this._hasInspectionReport = undefined;
		this._hideDamaged = undefined;
		this._hideNonOperable = undefined;
		this._onlyDamaged = undefined;
		this._onlyNonOperable = undefined;
		this._appraisalStatus = undefined;
		this._isBrandNew = false;
		this._searchText = undefined;
		this._conditionGrades = [];
		this._includeUngraded = false;
		this._regionCodes = [];
		this._provinceCodes = [];
		this._centerLat = undefined;
		this._centerLng = undefined;
		this._centerLabel = undefined;
		this._radiusKm = undefined;
	}

	// Create filter parameters for API call (uses API parameter names)
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	toQueryParams(): Record<string, any> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const params: Record<string, any> = {};

		if (this._makes.length > 0) params.makes = this._makes;
		if (this._models.length > 0) params.models = this._models;
		if (this._trims.length > 0) params.trim = this._trims; // API expects 'trim' (singular)
		if (this._suppliers.length > 0) params.suppliers = this._suppliers;
		if (this._fuelTypes.length > 0) params.fuelTypes = this._fuelTypes;
		if (this._transmissions.length > 0)
			params.transmission = this._transmissions; // API expects 'transmission' (singular)
		if (this._saleTypes.length > 0) params.dealType = this._saleTypes; // API expects 'dealType'
		if (this._colors.length > 0) params.colors = this._colors;
		if (this._doors.length > 0) params.doors = this._doors;
		if (this._seats.length > 0) params.seats = this._seats;

		if (this._minYear !== undefined && this._minYear > 0)
			params.minYear = this._minYear;
		if (this._maxYear !== undefined && this._maxYear > 0)
			params.maxYear = this._maxYear;
		if (this._minPrice !== undefined && this._minPrice > 0)
			params.minPrice = this._minPrice;
		if (this._maxPrice !== undefined && this._maxPrice > 0)
			params.maxPrice = this._maxPrice;
		if (this._minSellPrice !== undefined && this._minSellPrice > 0)
			params.minSellPrice = this._minSellPrice;
		if (this._maxSellPrice !== undefined && this._maxSellPrice > 0)
			params.maxSellPrice = this._maxSellPrice;
		if (this._minMileage !== undefined && this._minMileage > 0)
			params.minOdometer = this._minMileage; // API expects 'minOdometer'
		if (this._maxMileage !== undefined && this._maxMileage > 0)
			params.maxOdometer = this._maxMileage; // API expects 'maxOdometer'
		if (this._minEnginePower !== undefined && this._minEnginePower > 0)
			params.minEnginePower = this._minEnginePower;
		if (this._maxEnginePower !== undefined && this._maxEnginePower > 0)
			params.maxEnginePower = this._maxEnginePower;
		if (this._minCo2Emission !== undefined && this._minCo2Emission > 0)
			params.minCo2Emission = this._minCo2Emission;
		if (this._maxCo2Emission !== undefined && this._maxCo2Emission > 0)
			params.maxCo2Emission = this._maxCo2Emission;

		if (this._onlyFavorites) params.onlyFavorites = true;
		if (this._hasInspectionReport !== undefined)
			params.hasInspectionReport = this._hasInspectionReport;
		if (this._hideDamaged !== undefined) params.hideDamaged = this._hideDamaged;
		if (this._hideNonOperable !== undefined)
			params.hideNonOperable = this._hideNonOperable;
		if (this._onlyDamaged !== undefined) params.onlyDamaged = this._onlyDamaged;
		if (this._onlyNonOperable !== undefined)
			params.onlyNonOperable = this._onlyNonOperable;
		if (this._appraisalStatus) params.appraisalStatus = this._appraisalStatus;
		if (this._isBrandNew) params.suppliers = [NEW_CARS_SUPPLIER];
		if (this._searchText) params.searchText = this._searchText;

		// includeUngraded is only meaningful alongside selected grades — an
		// inactive grade filter must never narrow the feed.
		if (this._conditionGrades.length > 0) {
			params.conditionGrades = this._conditionGrades;
			if (this._includeUngraded) params.includeUngraded = true;
		}

		if (this._regionCodes.length > 0) params.regionCodes = this._regionCodes;
		if (this._provinceCodes.length > 0)
			params.provinceCodes = this._provinceCodes;
		if (
			this._centerLat !== undefined &&
			this._centerLng !== undefined &&
			this._radiusKm !== undefined
		) {
			params.centerLat = this._centerLat;
			params.centerLng = this._centerLng;
			params.radiusKm = this._radiusKm;
		}

		return params;
	}

	// Create URL parameters (uses internal property names for URL consistency)
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	toURLParams(): Record<string, any> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const params: Record<string, any> = {};

		if (this._makes.length > 0) params.makes = this._makes;
		if (this._models.length > 0) params.models = this._models;
		if (this._trims.length > 0) params.trims = this._trims;
		if (this._suppliers.length > 0) params.suppliers = this._suppliers;
		if (this._fuelTypes.length > 0) params.fuelTypes = this._fuelTypes;
		if (this._transmissions.length > 0)
			params.transmissions = this._transmissions; // Keep internal name for URL
		if (this._saleTypes.length > 0) params.saleTypes = this._saleTypes; // Keep internal name for URL
		if (this._colors.length > 0) params.colors = this._colors;
		if (this._doors.length > 0) params.doors = this._doors;
		if (this._seats.length > 0) params.seats = this._seats;
		if (this._markets.length > 0) params.markets = this._markets;

		if (this._minYear !== undefined && this._minYear > 0)
			params.minYear = this._minYear;
		if (this._maxYear !== undefined && this._maxYear > 0)
			params.maxYear = this._maxYear;
		if (this._minPrice !== undefined && this._minPrice > 0)
			params.minPrice = this._minPrice;
		if (this._maxPrice !== undefined && this._maxPrice > 0)
			params.maxPrice = this._maxPrice;
		if (this._minSellPrice !== undefined && this._minSellPrice > 0)
			params.minSellPrice = this._minSellPrice;
		if (this._maxSellPrice !== undefined && this._maxSellPrice > 0)
			params.maxSellPrice = this._maxSellPrice;
		if (this._minMileage !== undefined && this._minMileage > 0)
			params.minMileage = this._minMileage; // Keep internal name for URL
		if (this._maxMileage !== undefined && this._maxMileage > 0)
			params.maxMileage = this._maxMileage; // Keep internal name for URL
		if (this._minEnginePower !== undefined && this._minEnginePower > 0)
			params.minEnginePower = this._minEnginePower;
		if (this._maxEnginePower !== undefined && this._maxEnginePower > 0)
			params.maxEnginePower = this._maxEnginePower;
		if (this._minCo2Emission !== undefined && this._minCo2Emission > 0)
			params.minCo2Emission = this._minCo2Emission;
		if (this._maxCo2Emission !== undefined && this._maxCo2Emission > 0)
			params.maxCo2Emission = this._maxCo2Emission;

		if (this._onlyFavorites) params.onlyFavorites = true;
		if (this._hasInspectionReport !== undefined)
			params.hasInspectionReport = this._hasInspectionReport;
		if (this._hideDamaged !== undefined) params.hideDamaged = this._hideDamaged;
		if (this._hideNonOperable !== undefined)
			params.hideNonOperable = this._hideNonOperable;
		if (this._onlyDamaged !== undefined) params.onlyDamaged = this._onlyDamaged;
		if (this._onlyNonOperable !== undefined)
			params.onlyNonOperable = this._onlyNonOperable;
		if (this._appraisalStatus) params.appraisalStatus = this._appraisalStatus;
		if (this._isBrandNew) params.isBrandNew = true;
		if (this._searchText) params.searchText = this._searchText;

		if (this._conditionGrades.length > 0) {
			params.conditionGrades = this._conditionGrades;
			if (this._includeUngraded) params.includeUngraded = true;
		}

		if (this._regionCodes.length > 0) params.regionCodes = this._regionCodes;
		if (this._provinceCodes.length > 0)
			params.provinceCodes = this._provinceCodes;
		if (
			this._centerLat !== undefined &&
			this._centerLng !== undefined &&
			this._radiusKm !== undefined
		) {
			params.centerLat = this._centerLat;
			params.centerLng = this._centerLng;
			params.radiusKm = this._radiusKm;
			if (this._centerLabel) params.centerLabel = this._centerLabel;
		}

		return params;
	}

	// Helper to check if any filters are applied
	hasAppliedFilters(): boolean {
		return (
			this._makes.length > 0 ||
			this._models.length > 0 ||
			this._trims.length > 0 ||
			this._suppliers.length > 0 ||
			this._fuelTypes.length > 0 ||
			this._transmissions.length > 0 ||
			this._saleTypes.length > 0 ||
			this._colors.length > 0 ||
			this._doors.length > 0 ||
			this._seats.length > 0 ||
			this._markets.length > 0 ||
			(this._minYear !== undefined && this._minYear > 0) ||
			(this._maxYear !== undefined && this._maxYear > 0) ||
			(this._minPrice !== undefined && this._minPrice > 0) ||
			(this._maxPrice !== undefined && this._maxPrice > 0) ||
			(this._minSellPrice !== undefined && this._minSellPrice > 0) ||
			(this._maxSellPrice !== undefined && this._maxSellPrice > 0) ||
			(this._minMileage !== undefined && this._minMileage > 0) ||
			(this._maxMileage !== undefined && this._maxMileage > 0) ||
			(this._minEnginePower !== undefined && this._minEnginePower > 0) ||
			(this._maxEnginePower !== undefined && this._maxEnginePower > 0) ||
			(this._minCo2Emission !== undefined && this._minCo2Emission > 0) ||
			(this._maxCo2Emission !== undefined && this._maxCo2Emission > 0) ||
			this._onlyFavorites ||
			this._hasInspectionReport !== undefined ||
			this._hideDamaged !== undefined ||
			this._hideNonOperable !== undefined ||
			this._onlyDamaged !== undefined ||
			this._onlyNonOperable !== undefined ||
			this._appraisalStatus !== undefined ||
			this._isBrandNew ||
			(this._searchText !== undefined && this._searchText.length > 0) ||
			this._conditionGrades.length > 0 ||
			this.hasV2LocationFilter
		);
	}
}
